import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { NoteCreateRequest } from '../../../../types/api';
import { useNoteFormState, useNoteOperations } from './hooks';
import { NoteFormFields, NoteDateFields, NoteFileUpload } from './components';
import { useUploadAttachmentMutation } from '../statistics.mutations';

interface CreateNoteDialogProps {
  open: boolean;
  onClose: () => void;
  droneSerial: string;
  onNoteCreated?: () => void;
}

export const CreateNoteDialog: React.FC<CreateNoteDialogProps> = ({
  open,
  onClose,
  droneSerial,
  onNoteCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const { formData, errors, updateField, validateForm, resetForm } = useNoteFormState();

  const { createNote } = useNoteOperations(droneSerial);
  const uploadAttachmentMutation = useUploadAttachmentMutation(droneSerial);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Upload files if any are selected.
      // Only the S3 key is persisted; the download URL is fetched fresh on click
      // (presigned URLs expire after 1 hour).
      const attachments: Array<{ name: string; key: string }> = [];
      if (selectedFiles.length > 0) {
        setUploadingFiles(true);

        for (const file of selectedFiles) {
          try {
            const uploadedFile = await uploadAttachmentMutation.mutateAsync(file);
            attachments.push({
              name: file.name,
              key: uploadedFile.key,
            });
          } catch (uploadErr) {
            console.error('Error uploading file:', uploadErr);
            setError(`Failed to upload file: ${file.name}`);
            return;
          }
        }

        setUploadingFiles(false);
      }

      const noteData: NoteCreateRequest = {
        subject: formData.subject,
        content: formData.content,
        note_type: formData.note_type,
        priority: formData.priority,
        date_of_action: formData.date_of_action,
        attachments,
      };

      await createNote(noteData);

      // Reset form and close dialog
      resetForm();
      setSelectedFiles([]);

      // Clear file input
      const fileInput = document.getElementById('note-attachment-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      onClose();

      if (onNoteCreated) {
        onNoteCreated();
      }
    } catch (err) {
      console.error('Failed to create note:', err);
      setError(err instanceof Error ? err.message : 'Failed to create note');
    } finally {
      setLoading(false);
      setUploadingFiles(false);
    }
  };

  const handleClose = () => {
    if (!loading && !uploadingFiles) {
      resetForm();
      setSelectedFiles([]);
      setError(null);

      // Clear file input
      const fileInput = document.getElementById('note-attachment-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px' } }}
    >
      <DialogTitle>Create New Note</DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Form Fields */}
          <NoteFormFields
            formData={formData}
            errors={errors}
            onFieldChange={updateField}
            disabled={loading}
          />

          {/* Date Fields */}
          <NoteDateFields
            formData={formData}
            errors={errors}
            onFieldChange={updateField}
            disabled={loading || uploadingFiles}
          />

          {/* File Attachments */}
          <NoteFileUpload
            selectedFiles={selectedFiles}
            onFileSelect={handleFileSelect}
            onRemoveFile={handleRemoveFile}
            uploadingFiles={uploadingFiles}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading || uploadingFiles}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading || uploadingFiles || !formData.subject.trim() || !formData.content.trim()
          }
          startIcon={loading || uploadingFiles ? <CircularProgress size={20} /> : null}
        >
          {uploadingFiles ? 'Uploading...' : loading ? 'Creating...' : 'Create Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
